import React from 'react';

import { Button } from 'components/Button';
import { Dialog, DialogContent, DialogTitle } from 'components/Dialog';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

export interface ModalProps {
  open?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  okText?: string;
  cancelText?: string;
  width?: number | string;
  closable?: boolean | { 'aria-label': string };
  okButtonProps?: {
    disabled?: boolean;
    loading?: boolean;
    [key: string]: any;
  };
  cancelButtonProps?: {
    [key: string]: any;
  };
  className?: string;
  footer?: Maybe<React.ReactNode>;
  maskClosable?: boolean;
  centered?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  open = false,
  onOk,
  onCancel,
  title,
  children,
  okText = 'OK',
  cancelText = 'Cancel',
  width = 520,
  closable = true,
  okButtonProps = {},
  cancelButtonProps = {},
  className,
  footer,
  maskClosable = true,
  centered = false,
}) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && onCancel) {
      onCancel();
    }
  };

  const handleOk = () => {
    if (onOk) {
      onOk();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const showFooter = footer !== null;
  const defaultButtonFooter = (
    <div className='flex flex-row justify-end gap-2 border-t border-neutral-300 pt-4'>
      <Button
        variant='outline'
        onClick={handleCancel}
        buttonNamePosthog={null}
        {...cancelButtonProps}
      >
        {cancelText}
      </Button>
      <Button
        onClick={handleOk}
        disabled={okButtonProps?.disabled}
        buttonNamePosthog={null}
        {...okButtonProps}
      >
        {okText}
      </Button>
    </div>
  );

  const widthStyle =
    typeof width === 'number'
      ? {
          width: `min(${width}px, calc(100vw - 2rem))`,
          boxSizing: 'border-box' as const,
        }
      : { width };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'box-border max-w-lg min-w-0 gap-6 px-5 pb-5 pt-5',
          centered && 'items-center',
          className
        )}
        style={widthStyle}
        onInteractOutside={(e) => {
          if (!maskClosable) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!maskClosable || !closable) {
            e.preventDefault();
          }
        }}
      >
        {title && (
          <DialogTitle className='m-0 text-lg font-semibold text-neutral-900 pr-12 break-words'>
            {title}
          </DialogTitle>
        )}
        <div className='min-w-0'>{children}</div>
        {showFooter && (footer !== undefined ? footer : defaultButtonFooter)}
      </DialogContent>
    </Dialog>
  );
};

Modal.displayName = 'Modal';

export default Modal;
